import { differenceInSeconds, differenceInHours } from "date-fns";
import { CheckCircle, Clock, XCircle, MinusCircle } from "react-feather";
import { AgentInfo } from "../../../schema/types/agent/entity";
import { dateString } from "../../../components/dateStringFn";
import { formatStrings } from "../../../utils/time";
import { HEALTH_COLORS } from "../../health/utils";

export type InstanceStatus = "active" | "stale" | "inactive" | "never";

export interface ProcessedInstance {
  info: AgentInfo;
  status: InstanceStatus;
  lastActiveDate: Date | null;
  lastActiveText: string;
  lastActiveAbsolute: string;
}

export function getInstanceStatus(info: AgentInfo): InstanceStatus {
  if (!info.lastActive) return "never";
  const lastActiveDate = new Date(info.lastActive);
  if (isNaN(lastActiveDate.getTime())) return "never";
  const now = new Date();
  const diffSecs = differenceInSeconds(now, lastActiveDate);
  if (diffSecs <= 60) return "active";
  const diffHours = differenceInHours(now, lastActiveDate);
  if (diffHours <= 24) return "stale";
  return "inactive";
}

export const INSTANCE_STATUS_CONFIG: Record<
  InstanceStatus,
  {
    color: string;
    bg: string;
    icon: typeof CheckCircle;
    label: string;
  }
> = {
  active: { color: HEALTH_COLORS.ready, bg: "rgba(16, 185, 129, 0.1)", icon: CheckCircle, label: "Active" },
  stale: { color: HEALTH_COLORS.warning, bg: "rgba(245, 158, 11, 0.1)", icon: Clock, label: "Stale" },
  inactive: { color: HEALTH_COLORS.error, bg: "rgba(239, 68, 68, 0.1)", icon: XCircle, label: "Inactive" },
  never: { color: HEALTH_COLORS.inactive, bg: "rgba(107, 114, 128, 0.1)", icon: MinusCircle, label: "Never Active" },
};

export const WELL_KNOWN_ENV_KEYS: { key: string; label: string }[] = [
  { key: "hostname", label: "Hostname" },
  { key: "image", label: "Image" },
  { key: "tailscale_ip", label: "IP" },
  { key: "arch", label: "Arch" },
  { key: "os", label: "OS" },
  { key: "cloudProvider", label: "Cloud" },
  { key: "k8sVersion", label: "K8s" },
  { key: "username", label: "User" },
];

const STATUS_ORDER: Record<InstanceStatus, number> = { active: 0, stale: 1, inactive: 2, never: 3 };

export function sortInstances(instances: AgentInfo[]): ProcessedInstance[] {
  return instances
    .map((info) => {
      const status = getInstanceStatus(info);
      const lastActiveDate = info.lastActive ? new Date(info.lastActive) : null;
      const lastActiveText = info.lastActive
        ? dateString(info.lastActive, { format: formatStrings.humanizeWAgo })
        : "Never active";
      const lastActiveAbsolute = info.lastActive
        ? dateString(info.lastActive, { format: formatStrings.sec })
        : "";
      return { info, status, lastActiveDate, lastActiveText, lastActiveAbsolute };
    })
    .sort((a, b) => {
      const orderDiff = STATUS_ORDER[a.status] - STATUS_ORDER[b.status];
      if (orderDiff !== 0) return orderDiff;
      if (a.lastActiveDate && b.lastActiveDate) {
        return b.lastActiveDate.getTime() - a.lastActiveDate.getTime();
      }
      return a.lastActiveDate ? -1 : 1;
    });
}
